import { type BaseQueryFn, createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { z } from "zod";

import type { CustomBaseQueryArgs, CustomBaseQueryError } from "api/types/error";
import { handleSchemaFailure } from "api/utils";
import { logger } from "common/utilities/logger";
import { getIdToken } from "domains/authentication/selectors";
import { getSiteLanguage } from "domains/language/selectors";
import type { RootState } from "types/redux";

const customBaseQuery: BaseQueryFn<string | CustomBaseQueryArgs, unknown, CustomBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const baseQuery = fetchBaseQuery({
    baseUrl: `${import.meta.env.VITE_API_URL}/v1`,
    prepareHeaders: (headers, { getState }) => {
      const state = getState() as RootState;
      const idToken = getIdToken(state);
      const siteLanguage = getSiteLanguage(state);

      headers.set("accept-language", siteLanguage);
      headers.set("x-api-key", import.meta.env.VITE_API_KEY);
      if (idToken) headers.set("x-authorization", `Bearer ${idToken}`);

      return headers;
    },
  });

  const result = await baseQuery(args, api, extraOptions);

  if (typeof args === "object" && "schema" in args && args.schema && result.data) {
    try {
      const validatedData = args.schema.parse(result.data);
      return {
        data: validatedData,
      };
    } catch (error) {
      if (error instanceof z.ZodError) {
        const schemaErrorMessage = `Schema validation failed for endpoint: ${api.endpoint}`;
        logger.error(`[api.slice.ts] ${schemaErrorMessage}`, error);
        return { error: { status: "SCHEMA_VALIDATION_ERROR", data: error.issues, error: schemaErrorMessage } };
      }
    }
  }

  return result;
};

export const apiSlice = createApi({
  baseQuery: customBaseQuery,
  onSchemaFailure: handleSchemaFailure,
  tagTypes: ["CustomMaps", "CallToAction", "Sources", "Bookmarks", "Topics", "Knowledge", "Pinned"],
  endpoints: () => ({}),
});
